import React, { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Minus, Plus } from "lucide-react"
import { DateTime } from "luxon"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { numberFmt } from "@/util/numberFmt"

import { getFeedstockData } from "../api/getFeedstockData"
import { type FeedstockMonth, type FeedstockTableConfig } from "../types/feedstockTypes"
import { getMonthAverage } from "../util/getMonthAverage"

const columnHelper = createColumnHelper<FeedstockMonth>()

export default function FeedstockTable({ config }: { config: FeedstockTableConfig }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["feedstockData", { config }],
    queryFn: () => getFeedstockData(config),
  })

  const columns = useMemo(() => {
    const expandCol = columnHelper.display({
      id: "expand",
      header: () => <></>,
      cell: ({ row }) => (
        <div className="text-center px-2">
          <Button
            variant="ghost"
            disabled={!row.getCanExpand()}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? <Minus /> : <Plus />}
          </Button>
        </div>
      ),
    })
    const nameCol = columnHelper.accessor((month) => month.month, {
      id: "name",
      header: () => <div className="p-2 text-left">Month</div>,
      cell: (context) => <div className="p-2 px-4 text-left">{context.getValue()}</div>,
    })
    const valueCols = config.groups.map((g) =>
      columnHelper.group({
        header: g.name,
        columns: g.entities.map((e, i) =>
          columnHelper.accessor((month) => getMonthAverage(month, e.dimensionKey), {
            id: e.name,
            header: () => e.dimensionKey,
            cell: (context) => {
              const value = context.getValue()
              const textClass = config.bounds?.find((b) =>
                b.upper ? b.lower < value && b.upper >= value : b.lower < value,
              )?.class
              return (
                <div className={`py-2 px-4 ${i == 0 ? "border-l" : ""} ${textClass}`}>
                  {numberFmt(value)}
                </div>
              )
            },
          }),
        ),
      }),
    )

    const totalCol = columnHelper.accessor((month) => getMonthAverage(month, 0), {
      id: "total",
      header: () => <div className="w-[100px]">Total</div>,
      cell: (context) => <div className="py-2 px-4 border-l">{numberFmt(context.getValue())}</div>,
    })
    return [expandCol, nameCol, ...valueCols, totalCol]
  }, [config])

  const table = useReactTable<FeedstockMonth>({
    data: data?.rows || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => row.original.dates.length > 0,
  })

  if (isError) {
    return <>Network Error</>
  }

  return (
    <div className="rounded-md border shadow-sm h-[calc(100vh-175px)] overflow-x-auto ">
      <Table>
        <TableHeader className="sticky bg-card-header-light top-0 z-100">
          {table.getHeaderGroups().map((hg, index) => (
            <TableRow
              key={index}
              className={
                index == 0 ? "[&_th:first-child]:rounded-tl-md [&_th:last-child]:rounded-tr-md" : ""
              }
            >
              {hg.headers.map((header, i) => (
                <TableHead
                  key={i}
                  className={`px-4 py-2 bg-card-header ${
                    i != 0 && "text-center"
                  } ${index == 0 && "bg-card-header-light"} ${i == 0 && "w-[64px]"}`}
                  colSpan={header.colSpan}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isPending
            ? [...Array(12).keys()].map((i) => (
                <TableRow key={i}>
                  <TableCell
                    colSpan={config.groups.reduce((a, c) => a + c.entities.length, 0) + 3}
                    className="h-[52.5px]"
                  >
                    <Skeleton className="w-full h-full" />
                  </TableCell>
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow key={row.id} className="font-semibold">
                    {row.getVisibleCells().map((cell, i) => (
                      <TableCell
                        key={cell.id}
                        className={`px-0 py-2 ${i != 0 && "text-center"} ${
                          i == 1 && "sticky left-0 var(--table-bg)"
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() &&
                    row.original.dates.map((d, i) => {
                      return (
                        <TableRow key={i}>
                          <TableCell></TableCell>
                          <TableCell className="px-0 sticky left-0 var(--table-bg)">
                            <div className="p-2 px-4">
                              {DateTime.fromISO(d.date).toFormat("ccc L/d")}
                            </div>
                          </TableCell>
                          {config.groups.map((g, gi) => {
                            return g.entities.map((e, ei) => {
                              const val = d.values.find(
                                (kv) => kv.dimensionKey == e.dimensionKey && kv.type == "Actual",
                              )?.value
                              const textClass = val
                                ? config.bounds?.find((b) =>
                                    b.upper ? b.lower < val && b.upper >= val : b.lower < val,
                                  )?.class
                                : undefined
                              return (
                                <TableCell key={`${gi}-${ei}`} className={`px-0 ${textClass}`}>
                                  <div className={`py-2 px-0 text-center ${ei == 0 && "border-l"}`}>
                                    {val ? (
                                      numberFmt(val)
                                    ) : (
                                      <span className="text-gray-300 italic">N/A</span>
                                    )}
                                  </div>
                                </TableCell>
                              )
                            })
                          })}
                          <TableCell className="px-0">
                            <div className="border-l text-center py-2 px-4">
                              {d.values.find((v) => v.dimensionKey == 0) ? (
                                numberFmt(d.values.find((v) => v.dimensionKey == 0)?.value || 0)
                              ) : (
                                <span className="text-gray-300 italic">N/A</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </React.Fragment>
              ))}
        </TableBody>
      </Table>
    </div>
  )
}
